import {
  useAreas,
  useNowContext,
  useProjects,
  useServices,
  useTasks,
  useLiveQuery,
  useStore,
} from "@hq/client-react";
import { applyTaskView, builtInViews, todayTasks, projectProgress } from "@hq/core";
import { NotesTree, type NotesTreeHandle } from "@hq/notes-ui";
import { usePalette } from "@hq/command-palette";
import {
  useDeleteConfirmation,
  cn,
  ContextMenu,
  DropdownMenu,
  toast,
  IconButton,
  Input,
  RenameInput,
  Separator,
  Tooltip,
} from "@z/ui";
import {
  BookOpen,
  Box,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Inbox,
  LayoutTemplate,
  Layers,
  Folder,
  FolderPlus,
  Search,
  LayoutDashboard,
  Moon,
  PanelsTopLeft,
  Pencil,
  Plus,
  Settings,
  StickyNote,
  Sun,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import type { Project } from "@hq/contracts";
import { ProjectAreaDialog, ProjectProgress } from "@hq/tasks-ui";
import { NewAreaDialog } from "./NewAreaDialog";
import { ProjectOrder } from "./ProjectOrder";
import type { PaneSpec } from "../workspace/paneTypes";
import { useWorkspace } from "../workspace/WorkspaceContext";

function NavItem(props: {
  icon: LucideIcon;
  iconNode?: ReactNode;
  description?: string;
  label: string;
  count?: number;
  active: boolean;
  onClick: (pinned?: boolean) => void;
  onRename?: (name: string) => Promise<unknown>;
  actions?: ReactNode;
  shortcutId?: string;
  disabled?: boolean;
  indent?: boolean;
  iconColor?: string;
}) {
  const [renaming, setRenaming] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const Icon = props.icon;
  if (renaming && props.onRename)
    return (
      <div className="flex items-center gap-2.5 px-2.5 py-1">
        <Icon aria-hidden="true" className={cn("size-4 shrink-0", props.iconColor)} />
        <RenameInput
          initial={props.label}
          label={`Rename ${props.label}`}
          onSave={props.onRename}
          onClose={() => {
            setRenaming(false);
            requestAnimationFrame(() => buttonRef.current?.focus());
          }}
          className="h-7 min-w-0 border-accent ring-1 ring-accent text-sm"
        />
      </div>
    );
  const button = (
    <button
      type="button"
      ref={buttonRef}
      onClick={() => props.onClick()}
      onDoubleClick={() => props.onClick(true)}
      onKeyDown={(event) => {
        if (event.key === "F2" && props.onRename) {
          event.preventDefault();
          setRenaming(true);
        }
      }}
      disabled={props.disabled}
      title={props.description}
      aria-description={props.description}
      aria-current={props.active ? "page" : undefined}
      className={cn(
        "flex min-h-11 shrink-0 w-full items-center gap-2.5 lg:min-h-0 rounded-md px-2.5 py-2 text-left text-sm transition-colors duration-fast focus-ring",
        props.indent && "pl-6",
        props.active
          ? "bg-accent-muted font-medium text-accent"
          : "text-fg-muted hover:bg-surface-raised hover:text-fg",
        props.disabled && "cursor-default opacity-50 hover:bg-transparent",
      )}
    >
      {props.iconNode ?? (
        <Icon
          aria-hidden="true"
          strokeWidth={1.75}
          className={cn("size-4 shrink-0", props.iconColor)}
        />
      )}
      <span className="min-w-0 flex-1 truncate">{props.label}</span>
      {props.count !== undefined && props.count > 0 ? (
        <span className="min-w-5 text-center text-xs tabular-nums text-fg-muted">
          {props.count}
        </span>
      ) : null}
    </button>
  );
  const trigger = <ContextMenu.Trigger render={button} />;
  return (
    <ContextMenu.Root>
      {props.shortcutId ? (
        <Tooltip content={props.label} shortcutId={props.shortcutId} side="right">
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      <ContextMenu.Content
        aria-label={`Actions for ${props.label}`}
        finalFocus={() => buttonRef.current ?? false}
      >
        <ContextMenu.Item disabled={props.disabled} onClick={() => props.onClick()}>
          Open
        </ContextMenu.Item>
        <ContextMenu.Item disabled={props.disabled} onClick={() => props.onClick(true)}>
          <PanelsTopLeft /> Open in new tab
        </ContextMenu.Item>
        {props.onRename && (
          <>
            <ContextMenu.Separator />
            <ContextMenu.Item onClick={() => setRenaming(true)}>
              <Pencil /> Rename
              <ContextMenu.Shortcut keys="F2" />
            </ContextMenu.Item>
          </>
        )}
        {props.actions}
      </ContextMenu.Content>
    </ContextMenu.Root>
  );
}

function useSidebarExpansion(key: string) {
  const store = useStore();
  const { data } = useLiveQuery(["kv"], (s) => s.kv.get<boolean>(`sidebar.expanded.${key}`), [key]);
  const open = data ?? true;
  return [
    open,
    (value: boolean | ((previous: boolean) => boolean)) => {
      void store.kv.set(
        `sidebar.expanded.${key}`,
        typeof value === "function" ? value(open) : value,
      );
    },
  ] as const;
}

function ProjectAreaGroup({ area, children }: { area: string; children: ReactNode }) {
  const [open, setOpen] = useSidebarExpansion(`area.${area}`);
  if (!area) return <>{children}</>;
  return (
    <section aria-label={`Area: ${area}`}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="group/area flex min-h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-left text-sm font-semibold text-fg hover:bg-surface-raised focus-ring"
      >
        <Box aria-hidden="true" strokeWidth={1.75} className="size-4 shrink-0 text-fg-faint" />
        <span className="min-w-0 flex-1 truncate">{area}</span>
        <ChevronRight
          aria-hidden="true"
          className={cn("size-3 text-fg-faint transition-transform", open && "rotate-90")}
        />
      </button>
      <div hidden={!open}>{children}</div>
    </section>
  );
}

function Section(props: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  canDrop?: () => boolean;
  onDrop?: () => void;
}) {
  const [open, setOpen] = useSidebarExpansion(props.title);
  const [dropActive, setDropActive] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="flex shrink-0 flex-col gap-0.5">
      <div
        className={cn(
          "flex items-center justify-between rounded-md px-1",
          dropActive && "bg-accent-muted ring-1 ring-accent",
        )}
        onDragOver={(event) => {
          if (!props.canDrop?.()) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          setDropActive(true);
        }}
        onDragLeave={() => setDropActive(false)}
        onDrop={(event) => {
          setDropActive(false);
          if (!props.canDrop?.()) return;
          event.preventDefault();
          props.onDrop?.();
          setOpen(true);
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 rounded-sm px-1 py-1 text-fg-muted text-xs font-medium tracking-wide hover:text-fg focus-ring"
          aria-expanded={open}
        >
          <Chevron className="size-3" />
          {props.title}
        </button>
        <div className="flex items-center" onClickCapture={() => setOpen(true)}>
          {props.action}
        </div>
      </div>
      <div className={cn("flex flex-col gap-0.5", !open && "hidden")}>{props.children}</div>
    </div>
  );
}

function SidebarHeader({ onSearch }: { onSearch: () => void }) {
  return (
    <div className="mb-1 flex shrink-0 items-center gap-1">
      <Tooltip content="Search workspace" shortcutId="palette.toggle">
        <button
          type="button"
          onClick={onSearch}
          className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-md bg-bg/60 px-2.5 text-sm text-fg-muted transition-colors hover:bg-surface-raised focus-ring"
          aria-label="Search workspace"
        >
          <Search aria-hidden="true" className="size-4 shrink-0" />
          <span className="truncate text-left">Search anything</span>
        </button>
      </Tooltip>
    </div>
  );
}

/**
 * Navigation grouped by object type: Todos, Notes, Calendar. Every item opens or activates a
 * pane; navigation replaces the focused unpinned pane, so the tab strip stays short.
 */
export function Sidebar({
  className,
  onNavigate,
  collapsed = false,
  id: sidebarId,
}: {
  className?: string;
  onNavigate?: () => void;
  collapsed?: boolean;
  id?: string;
}) {
  const ws = useWorkspace();
  const palette = usePalette();
  const notesTree = useRef<NotesTreeHandle>(null);
  const services = useServices();
  const { requestDelete, deleteDialog } = useDeleteConfirmation();
  const now = useNowContext();
  const { data: tasks } = useTasks();
  const progress = projectProgress(tasks ?? []);
  const { data: projects } = useProjects({ status: ["active"] });
  const { data: areas = [] } = useAreas();
  const [newAreaOpen, setNewAreaOpen] = useState(false);
  const [newProject, setNewProject] = useState<string | null>(null);

  const [areaProject, setAreaProject] = useState<Project | null>(null);
  const open = (tasks ?? []).filter((task) => task.status === "open");
  const inboxCount = applyTaskView(open, builtInViews.inbox, now).length;
  const t = todayTasks(open, now);
  const todayCount = t.overdue.length + t.today.length + t.evening.length;
  const focused = ws.focused;
  const focusedType = focused?.paneType;
  const focusedProject = focusedType === "project" ? String(focused?.params.projectId) : undefined;
  const focusedNote = focusedType === "note" ? String(focused?.params.noteId) : null;
  const nav =
    (spec: PaneSpec) =>
    (pinned = false) => {
      ws.open(spec, { pinned, mode: pinned ? "tab" : "replace" });
      onNavigate?.();
    };

  const createProject = async () => {
    const name = newProject?.trim();
    setNewProject(null);
    if (!name) return;
    const project = await services.projects.create({ name });
    ws.open({ paneType: "project", projectId: project.id });
    onNavigate?.();
  };

  return (
    <nav
      aria-label="Workspace"
      id={sidebarId}
      hidden={collapsed}
      className={cn(
        "flex h-full min-h-0 w-60 shrink-0 flex-col gap-3 overflow-hidden select-none border-r border-border bg-surface px-3 py-4 [&[hidden]]:hidden",
        className,
      )}
    >
      <SidebarHeader
        onSearch={() => {
          onNavigate?.();
          palette.open();
        }}
      />
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain pb-2">
        <Section title="To-dos">
          <NavItem
            icon={Sun}
            iconColor="text-warning"
            label="Today"
            count={todayCount}
            active={focusedType === "today"}
            onClick={nav({ paneType: "today" })}
            shortcutId="go.today"
          />
          <NavItem
            icon={Inbox}
            iconColor="text-accent"
            label="Inbox"
            count={inboxCount}
            active={focusedType === "inbox"}
            onClick={nav({ paneType: "inbox" })}
            shortcutId="go.inbox"
          />
          <NavItem
            icon={CalendarDays}
            iconColor="text-nav-coral"
            label="Upcoming"
            active={focusedType === "upcoming"}
            onClick={nav({ paneType: "upcoming" })}
            shortcutId="go.upcoming"
          />
          <NavItem
            icon={Layers}
            iconColor="text-nav-teal"
            label="Anytime"
            active={focusedType === "anytime"}
            onClick={nav({ paneType: "anytime" })}
            shortcutId="go.anytime"
          />
          <NavItem
            icon={Moon}
            iconColor="text-nav-purple"
            label="Someday"
            active={focusedType === "someday"}
            onClick={nav({ paneType: "someday" })}
          />
          <NavItem
            icon={CheckCircle2}
            iconColor="text-success"
            label="Completed"
            active={focusedType === "completed"}
            onClick={nav({ paneType: "completed" })}
          />
          <div className="flex flex-col gap-2">
            <ProjectOrder
              projects={projects ?? []}
              areas={areas}
              header={
                <div className="flex h-8 items-center gap-2">
                  <Separator className="flex-1" />
                  <DropdownMenu.Root>
                    <Tooltip content="New project or area">
                      <DropdownMenu.Trigger
                        render={
                          <IconButton label="New project or area" size="sm">
                            <Plus />
                          </IconButton>
                        }
                      />
                    </Tooltip>
                    <DropdownMenu.Content align="end">
                      <DropdownMenu.Item onClick={() => setNewProject("")}>
                        <FolderPlus /> New project
                      </DropdownMenu.Item>
                      <DropdownMenu.Item onClick={() => setNewAreaOpen(true)}>
                        <Box /> New area
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                </div>
              }
              renderArea={(area, children) => (
                <ProjectAreaGroup area={area}>{children}</ProjectAreaGroup>
              )}
            >
              {(p) => {
                const count = progress.get(p.id) ?? { completed: 0, total: 0 };
                return (
                  <NavItem
                    key={p.id}
                    icon={Folder}
                    iconNode={<ProjectProgress {...count} />}
                    description={`${count.completed} of ${count.total} todos completed`}
                    label={p.name}
                    active={focusedProject === p.id}
                    onClick={nav({ paneType: "project", projectId: p.id })}
                    onRename={(name) => services.projects.rename(p.id, name)}
                    actions={
                      <>
                        <ContextMenu.Item onClick={() => setAreaProject(p)}>
                          <Box /> Move to area…
                        </ContextMenu.Item>
                        <ContextMenu.Item
                          onClick={() => {
                            void services.projects
                              .archive(p.id)
                              .then(() =>
                                toast({
                                  title: "Project archived",
                                  action: {
                                    label: "Undo",
                                    onClick: () => void services.projects.unarchive(p.id),
                                  },
                                }),
                              )
                              .catch(() =>
                                toast({
                                  title: "Could not archive project",
                                  variant: "danger",
                                }),
                              );
                          }}
                        >
                          Archive project
                        </ContextMenu.Item>
                        <ContextMenu.Separator />
                        <ContextMenu.Item
                          variant="destructive"
                          onClick={() => {
                            requestDelete({
                              name: p.name,
                              description:
                                "Its tasks won't be deleted. You can restore this project from Trash.",
                              onConfirm: async () => {
                                await services.projects.delete(p.id);
                                toast({
                                  title: "Project moved to Trash",
                                  action: {
                                    label: "Undo",
                                    onClick: () => void services.projects.restore(p.id),
                                  },
                                });
                              },
                            });
                          }}
                        >
                          Move to Trash
                        </ContextMenu.Item>
                      </>
                    }
                  />
                );
              }}
            </ProjectOrder>
            {(projects?.length ?? 0) === 0 && newProject === null && (
              <button
                type="button"
                onClick={() => setNewProject("")}
                className="rounded-md px-2.5 py-1.5 text-left text-xs text-fg-faint hover:text-fg focus-ring"
              >
                Create a project…
              </button>
            )}
          </div>
          {newProject !== null ? (
            <form
              className="px-1"
              onSubmit={(e) => {
                e.preventDefault();
                void createProject();
              }}
            >
              <Input
                autoFocus
                value={newProject}
                placeholder="Project name"
                aria-label="Project name"
                onChange={(e) => setNewProject(e.target.value)}
                onBlur={() => void createProject()}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setNewProject(null);
                }}
              />
            </form>
          ) : null}
        </Section>

        <Separator />
        <Section
          title="Notes"
          canDrop={() => notesTree.current?.canDropAtRoot() ?? false}
          onDrop={() => void notesTree.current?.dropAtRoot()}
          action={
            <>
              <Tooltip content="New folder">
                <IconButton
                  label="New folder"
                  size="sm"
                  onClick={() => void notesTree.current?.createFolder()}
                >
                  <FolderPlus />
                </IconButton>
              </Tooltip>
              <Tooltip content="New note" shortcutId="notes.new">
                <IconButton
                  label="New note"
                  size="sm"
                  onClick={() =>
                    void services.notes.create({}).then((n) => {
                      ws.openNote(n.id, { pinned: true, autoFocus: true });
                      onNavigate?.();
                    })
                  }
                >
                  <Plus />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <NavItem
            icon={BookOpen}
            iconColor="text-nav-teal"
            label="Daily Notes"
            active={focusedType === "daily"}
            onClick={nav({ paneType: "daily" })}
            shortcutId="go.daily"
          />
          <NavItem
            icon={StickyNote}
            iconColor="text-warning"
            label="All Notes"
            active={focusedType === "allNotes"}
            onClick={nav({ paneType: "allNotes" })}
            shortcutId="go.notes"
          />
          <NavItem
            icon={LayoutTemplate}
            iconColor="text-nav-purple"
            label="Templates"
            active={focusedType === "templates"}
            onClick={nav({ paneType: "templates" })}
          />
          <NotesTree
            ref={notesTree}
            variant="navigation"
            selectedNoteId={focusedNote}
            selectedFolderId={focusedType === "folder" ? String(focused?.params.folderId) : null}
            onOpenNote={(id, options) => {
              ws.openNote(id, options);
              onNavigate?.();
            }}
            onOpenFolder={(id, options) => {
              ws.open(
                { paneType: "folder", folderId: id },
                {
                  ...options,
                  mode: options?.pinned ? "tab" : "replace",
                },
              );
              onNavigate?.();
            }}
            search={false}
          />
        </Section>

        <Separator />
        <div className="flex shrink-0 flex-col gap-0.5">
          <NavItem
            icon={CalendarDays}
            iconColor="text-nav-coral"
            label="Calendar"
            active={focusedType === "calendar"}
            onClick={nav({ paneType: "calendar" })}
          />
        </div>

        <Separator />
        <NavItem
          icon={LayoutDashboard}
          iconColor="text-nav-teal"
          label="Dashboards"
          active={focusedType === "dashboards"}
          onClick={nav({ paneType: "dashboards" })}
        />
      </div>
      <div className="flex shrink-0 flex-col gap-0.5 border-t border-border pt-3">
        <NavItem
          icon={Trash2}
          label="Trash"
          active={focusedType === "trash"}
          onClick={nav({ paneType: "trash" })}
        />
        <NavItem
          icon={Settings}
          label="Settings"
          active={focusedType === "settings"}
          onClick={nav({ paneType: "settings" })}
        />
      </div>
      {newAreaOpen && <NewAreaDialog onClose={() => setNewAreaOpen(false)} />}
      {areaProject && (
        <ProjectAreaDialog project={areaProject} onClose={() => setAreaProject(null)} />
      )}
      {deleteDialog}
    </nav>
  );
}
